import {
  AppError,
  allProcedures,
  type AppClient,
  type DemoData,
  type ProcedureDef,
} from '@pointfive/contracts';
import { composeDataset } from '@pointfive/sim';

/** `@pointfive/client` — the tRPC-over-HTTP binding of `AppClient`.
 *
 *  Built from the contracts rather than hand-written, for the same reason the router is: every declared procedure
 *  exists on the client by construction, with the verb the contract declares, and one that does not exist on the
 *  server produces a typed error rather than a silent `undefined is not a function`.
 *
 *  Errors cross the wire as their `AppError` shape under `error.data.app`, so a caller branches on `code` and reads
 *  `retryAfter` or `requiredTier` exactly as it did against the in-process sim. That is the whole point of the swap:
 *  the transport changes, the calls and their failures do not.
 */

export interface HttpClientOptions {
  /** API origin, e.g. `http://localhost:3000`. */
  readonly baseUrl: string;
  /** Injected by tests; defaults to the platform `fetch`. */
  readonly fetchImpl?: typeof fetch;
  /** Extra headers per request — used for the test harness's session cookie. */
  readonly headers?: () => Record<string, string>;
  /** Reported as the aggregate's provenance. See `datasetMode` below. */
  readonly datasetMode?: 'simulated' | 'live';
}

export type HttpClient = AppClient & {
  readonly mode: 'live';
  readonly demo: { dataset: () => Promise<DemoData> };
};

interface TrpcEnvelope {
  result?: { data?: unknown };
  error?: { data?: { app?: unknown }; message?: string };
}

export function createHttpClient(options: HttpClientOptions): HttpClient {
  const doFetch = options.fetchImpl ?? fetch;
  const base = options.baseUrl.replace(/\/$/, '');

  async function invoke(name: string, def: ProcedureDef, input: unknown): Promise<unknown> {
    // The batched wire format is used for every call, including single ones: it is the only shape that can express
    // "no input" (an absent key) as distinct from "an empty object", which a `z.void()` procedure rejects.
    const batch = input === undefined ? {} : { 0: input };
    const headers: Record<string, string> = {
      'content-type': 'application/json',
      ...(options.headers?.() ?? {}),
    };

    const url =
      def.kind === 'query'
        ? `${base}/api/trpc/${name}?batch=1&input=${encodeURIComponent(JSON.stringify(batch))}`
        : `${base}/api/trpc/${name}?batch=1`;

    const response = await doFetch(url, {
      method: def.kind === 'query' ? 'GET' : 'POST',
      headers,
      credentials: 'include',
      ...(def.kind === 'query' ? {} : { body: JSON.stringify(batch) }),
    });

    let body: unknown;
    try {
      body = await response.json();
    } catch {
      throw new AppError({
        code: 'UPSTREAM_DEGRADED',
        message: 'The server returned a response we could not read.',
        source: 'api',
      });
    }

    const entry = (Array.isArray(body) ? body[0] : body) as TrpcEnvelope | undefined;
    if (entry?.error) {
      const app = AppError.from(entry.error.data?.app);
      if (app) throw app;
      throw new AppError({
        code: 'INTERNAL',
        message: entry.error.message ?? 'Something went wrong.',
        requestId: response.headers.get('x-request-id') ?? 'unknown',
      });
    }
    if (!response.ok)
      throw new AppError({
        code: 'UPSTREAM_DEGRADED',
        message: `The server refused the request (${response.status}).`,
        source: 'api',
      });
    return entry?.result?.data;
  }

  const root: Record<string, Record<string, Record<string, (input?: unknown) => Promise<unknown>>>> = {};
  for (const [name, def] of Object.entries(allProcedures) as [string, ProcedureDef][]) {
    const [domain, module, verb] = name.split('.');
    if (!domain || !module || !verb) throw new Error(`Malformed procedure name: ${name}`);
    const modules = (root[domain] ??= {});
    const verbs = (modules[module] ??= {});
    verbs[verb] = (input?: unknown) => invoke(name, def, input);
  }

  const client = { ...root, mode: 'live' as const } as unknown as AppClient & { mode: 'live' };

  return {
    ...client,
    mode: 'live',
    demo: {
      /** The aggregate the Phase-1 screens read, composed from the family procedures over HTTP.
       *
       *  Composing on the client rather than adding a server endpoint keeps every read inside the cascade — an
       *  aggregate endpoint would be a second path into the data, and the first thing to skip a check. It also means
       *  the aggregate is provably the same composition the sim performed, which is what makes the swap safe. */
      dataset: () => composeDataset(client, 'live', options.datasetMode ?? 'simulated'),
    },
  };
}
